import { Button, CloseButton, Dialog, Field, Fieldset, Input, Portal, Text } from '@chakra-ui/react';
import { toaster } from 'provider/src/module/chakra-ui/components/ui/toaster.tsx';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import ApplicationErrorView from '@packages/charka-common-ui/graphql/error/application-error-view';
import { useCreatePageRule } from '../logic/use-create-page-rule';
import SectionMultipleSelect from './section-multiple-select';
import PrinterDeviceSelection from './printer-device-multiple-device-selection';

interface FormValues {
  name: string;
  menuSectionIds: string[];
  printerDeviceId?: string;
}

export default function CreatePageRule() {
  const { t } = useTranslate();
  const ruleId = useParams().ruleId ?? '';

  const [create, { error, loading }] = useCreatePageRule();

  const [open, setOpen] = useState(false);
  const {
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      menuSectionIds: [],
      printerDeviceId: undefined,
    },
  });

  const onSubmit = () => {
    create({
      variables: {
        input: {
          name: watch('name'),
          ruleId,
          printerDeviceId: watch('printerDeviceId') ?? null,
          allowedSections: watch('menuSectionIds').map((item) => ({
            menuSectionId: item,
          })),
        },
      },
    })
      .then(() => {
        toaster.create({
          description: t('page-rule.create.alert', 'Create page rule successful'),
          type: 'info',
        });
        reset({
          name: '',
          menuSectionIds: [],
        });
        setOpen(false);
      })
      .catch(() => {
        toaster.create({
          description: t('page-rule.create.alert.failed', 'Create page rule failed'),
          type: 'error',
        });
      });
  };

  const FieldSet = (
    <Fieldset.Root size="lg" maxW="md">
      <Fieldset.Content>
        <Field.Root>
          <Field.Label>{t('print-rule.create.name', 'Name')}</Field.Label>
          <Input
            {...register('name', { required: t('print-rule.create.name-required', 'Name is required') })}
            name="name"
          />

          {errors.name && (
            <Text color="red.500" fontSize="sm">
              {errors.name.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('print-rule.create.section', 'Allowed sections')}</Field.Label>
          <SectionMultipleSelect
            allowedSections={watch('menuSectionIds')}
            onChange={(item: string[]) => {
              setValue('menuSectionIds', item);
            }}
          />
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('print-rule.create.printer', 'Printer devices')}</Field.Label>
          <PrinterDeviceSelection
            printerDeviceId={watch('printerDeviceId')}
            onChange={(printerId: string) => {
              setValue('printerDeviceId', printerId);
            }}
          />
        </Field.Root>
      </Fieldset.Content>
    </Fieldset.Root>
  );

  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={(e) => {
        setOpen(e.open);
      }}
      placement={'center'}
    >
      <Dialog.Trigger asChild>
        <Button variant={'surface'} p={2} bgColor={'gray.700'} color={'white'}>
          {t('page-rule.button.add-page', 'Add page')}
        </Button>
      </Dialog.Trigger>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title fontSize={'xl'}>{t('page-rule.dialog.create.title', 'Create Page Rule')}</Dialog.Title>
            </Dialog.Header>

            <ApplicationErrorView error={error} />

            <Dialog.Body>{FieldSet}</Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="surface" p={2}>
                  {t('common.button.cancel', 'Cancel')}
                </Button>
              </Dialog.ActionTrigger>
              <Button
                loading={loading}
                onClick={() => {
                  void handleSubmit(onSubmit)();
                }}
                p={2}
                bg={'gray.700'}
                variant={'surface'}
                color={'white'}
                type="submit"
              >
                {t('common.button.create')}
              </Button>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
